import { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Breadcrumbs, Typography } from '@mui/material';
import LocationService from '../../shared/services/location/location.service';
import GroupService from '../../shared/services/group/group.service';
import { IKeyValue } from '../../shared/interfaces/generic-objects';
import VehicleService from '../../shared/services/vehicle/vehicle.service';
import ProjectService from '../../shared/services/project/project.service';
import ContactSupplierService from '../../shared/services/contact-supplier/contact-supplier.service';
import SubsidiaryService from '../../shared/services/subsidiary/subsidiary.service';
import { UserDetailsContext } from '../../context/user-details/user-details';
import { ScreenLabelsContext } from '../../context/screen-labels/screen-labels';
import {
  CLOUD_PROVIDER_NAMES,
  CloudEmissionsProvider,
  isCloudEmissionsProvider,
} from '../../modules/third-party-connection/shared-services/cloud-emissions/cloud-emissions.model';

interface IStep {
  to: string;
  name: string;
}

export const Breadcrumb = () => {
  const userDetailsContext = useContext(UserDetailsContext);
  const screenLabelsContext = useContext(ScreenLabelsContext);
  const groupService = GroupService.getInstance();
  const locationService = LocationService.getInstance();
  const vehicleService = VehicleService.getInstance();
  const projectService = ProjectService.getInstance();
  const contactSupplierService = ContactSupplierService.getInstance();
  const subsidiaryService = SubsidiaryService.getInstance();
  const location = useLocation();
  const [stepsArray, setStepsArray] = useState<IStep[]>([]);
  const [dataset, setDataset] = useState<IKeyValue[]>([]);

  useEffect(() => {
    const path = location.pathname.split('/');
    path.shift();

    if (location.pathname !== '/') {
      const formatted = path.map((element, index) => {
        let route = '';
        let name = '';
        for (let i = 0; i <= index; i++) {
          route += '/';
          const data = dataset?.find(data => data.key === path[i]);

          const regEx = /[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/;
          const isUUID = regEx.test(path[i]);

          if (data) {
            route += path[i];
            name = data.value;
          } else if (isUUID) {
            route += path[i];
            name = 'Details';
          } else {
            route += path[i];
            name = path[i];
          }

          if (isUUID && path[index - 1] === 'secr-report') {
            name = 'Report detail';
          } else if (isUUID && path[index - 1] === 'secr') {
            name = 'Emission detail';
          }
        }

        // Keep these crumbs in step with the renamed nav entries; the raw path
        // segments ("financial", "reports") say less than the nav labels do.
        if (path[index] === 'financial' && path[index - 1] === 'emissions') {
          name = 'Spend Based Emissions';
        } else if (path[index] === 'reports' && path[index - 1] === 'analytics') {
          name = 'Saved Reports';
        } else if (path[index] === 'businesscentral' && path[index - 1] === 'integrations') {
          name = 'Dynamics 365 Business Central';
        } else if (path[index - 1] === 'integrations' && isCloudEmissionsProvider(path[index])) {
          // The vendor's product name, not the route's code ("aws-sustainability").
          name = CLOUD_PROVIDER_NAMES[path[index] as CloudEmissionsProvider];
        } else if (path[index] === 'products' && path[index - 1] === 'organisation') {
          // Matches the split inner nav; a custom "product" screen label folds in.
          const productsLabel = screenLabelsContext.getLabel('product', { plural: true, capitalize: 'first' });
          name = productsLabel && productsLabel !== 'Products'
            ? `Purchased & Capital ${productsLabel}`
            : 'Purchased & Capital Goods';
        } else if (path[index] === 'sold-products' && path[index - 1] === 'organisation') {
          const productsLabel = screenLabelsContext.getLabel('product', { plural: true, capitalize: 'first' });
          name = productsLabel && productsLabel !== 'Products'
            ? `Sold ${productsLabel}`
            : 'Sold Products';
        }

        const nameArray = name.split('-');
        for (let i = 0; i < nameArray.length; i++) {
          if (nameArray[i] === 'secr') {
            nameArray[i] = nameArray[i].toUpperCase();
          } else {
            const term = nameArray[i].toLowerCase();
            if (term === 'locations') {
              nameArray[i] = screenLabelsContext.getLabel('location', { plural: true, capitalize: 'first' });
            } else if (term === 'location') {
              nameArray[i] = screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' });
            } else if (term === 'vehicles') {
              nameArray[i] = screenLabelsContext.getLabel('vehicle', { plural: true, capitalize: 'first' });
            } else if (term === 'vehicle') {
              nameArray[i] = screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' });
            } else if (term === 'subsidiaries') {
              nameArray[i] = screenLabelsContext.getLabel('subsidiary', { plural: true, capitalize: 'first' });
            } else if (term === 'subsidiary') {
              nameArray[i] = screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' });
            } else if (term === 'projects') {
              nameArray[i] = screenLabelsContext.getLabel('project', { plural: true, capitalize: 'first' });
            } else if (term === 'project') {
              nameArray[i] = screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' });
            } else if (term === 'suppliers') {
              nameArray[i] = screenLabelsContext.getLabel('supplier', { plural: true, capitalize: 'first' });
            } else if (term === 'supplier') {
              nameArray[i] = screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' });
            } else if (term === 'organisations') {
              nameArray[i] = screenLabelsContext.getLabel('organisation', { plural: true, capitalize: 'first' });
            } else if (term === 'organisation') {
              nameArray[i] = screenLabelsContext.getLabel('organisation', { plural: false, capitalize: 'first' });
            } else if (term === 'products') {
              nameArray[i] = screenLabelsContext.getLabel('product', { plural: true, capitalize: 'first' });
            } else if (term === 'product') {
              nameArray[i] = screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' });
            } else {
              nameArray[i] = nameArray[i][0].toUpperCase() + nameArray[i].slice(1);
            }
          }
        }

        return {
          to: route,
          name: nameArray.join(' '),
        };
      });

      setStepsArray(formatted);
    }
  }, [location.pathname, dataset, screenLabelsContext]);

  useEffect(() => {
    if (!userDetailsContext.userDetails?.emailVerificationRequired) {
      Promise.all([
        locationService.getAllLocations(),
        vehicleService.getAllVehicles(),
        groupService.getGroups(),
        projectService.getAll(),
        contactSupplierService.getAllSuppliersLite(),
        subsidiaryService.getAll(),
      ])
        .then(([locationResponse, vehicleResponse, groupResponse, projectResponse, supplierResponse, subsidiaryResponse]) => {
          const locationsData = locationResponse.map(location => ({
            key: location.id,
            value: location.friendlyName,
          }));

          const vehiclesData = vehicleResponse.map(vehicle => ({
            key: vehicle.id,
            value: vehicle.customUniqueId,
          }));

          const groupsData = groupResponse.map(group => ({
            key: group.id,
            value: group.title,
          }));

          const projectsData = projectResponse.map(project => ({
            key: project.id,
            value: project.summary,
          }));

          const suppliersData = supplierResponse.map(supplier => ({
            key: supplier.id,
            value: supplier.name,
          }));

          const subsidiariesData = subsidiaryResponse.map(subsidiary => ({
            key: subsidiary.id,
            value: subsidiary.name,
          }));

          setDataset([...locationsData, ...vehiclesData, ...groupsData, ...projectsData, ...suppliersData, ...subsidiariesData]);
        })
        .catch(err => console.log(err));
    }
  }, [userDetailsContext.userDetails]);

  return (
    <>
      {stepsArray.length > 0 && dataset && (
        <Breadcrumbs aria-label='breadcrumb'>
          {stepsArray.map((step, index) => {
            return (
              <div key={index}>
                {index !== stepsArray.length - 1 && (
                  <Link to={step.to} style={{ textDecoration: 'none' }}>
                    <Typography
                      variant='body1'
                      noWrap
                      component='div'
                      color='text.primary'
                      sx={{ fontWeight: 'bold' }}
                    >
                      {step.name}
                    </Typography>
                  </Link>
                )}
                {index === stepsArray.length - 1 && (
                  <Typography
                    variant='body1'
                    noWrap
                    component='div'
                    color='text.primary'
                    sx={{ fontWeight: 'bold' }}
                  >
                    {step.name}
                  </Typography>
                )}
              </div>
            );
          })}
        </Breadcrumbs>
      )}
    </>
  );
};
